import type { ReactNode } from 'react'

import { clsx } from 'clsx'

import type { SelectOption } from '../searchable-select'

// ============================================================================
// Chip remove button
// ============================================================================

interface ChipRemoveButtonProps {
  label: string
  onRemove: () => void
}

export function ChipRemoveButton({ label, onRemove }: ChipRemoveButtonProps): JSX.Element {
  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      onRemove()
    }
  }
  return (
    <span
      aria-label={label}
      className="flex-shrink-0 hover:bg-muted rounded cursor-pointer"
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); onRemove() }}
      onKeyDown={handleKeyDown}
    >
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path
          clipRule="evenodd"
          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
          fillRule="evenodd"
        />
      </svg>
    </span>
  )
}

// ============================================================================
// Default chip
// ============================================================================

interface DefaultChipProps<T> {
  option: SelectOption<T>
  onRemove: () => void
}

export function DefaultChip<T>({ option, onRemove }: DefaultChipProps<T>): JSX.Element {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-md',
        'bg-accent text-muted-foreground text-sm truncate max-w-[150px]',
        'border border-border'
      )}
    >
      <span className="truncate">{option.label}</span>
      <ChipRemoveButton label={`Remove ${option.label}`} onRemove={onRemove} />
    </span>
  )
}

// ============================================================================
// Selected content area
// ============================================================================

interface SelectedContentProps<T> {
  onRemoveChip: (value: T) => void
  placeholder: string
  renderChip?: (option: SelectOption<T>, onRemove: () => void) => ReactNode
  selectedOptions: SelectOption<T>[]
  showCount: boolean
  textClass: string
}

export function SelectedContent<T>({
  onRemoveChip, placeholder, renderChip, selectedOptions, showCount, textClass,
}: SelectedContentProps<T>): JSX.Element {
  if (selectedOptions.length === 0) {
    return <span className={clsx('text-muted-foreground truncate', textClass)}>{placeholder}</span>
  }
  if (showCount) {
    return <span className={clsx('truncate', textClass)}>{selectedOptions.length} selected</span>
  }
  return (
    <>
      {selectedOptions.map((option) =>
        renderChip
          ? renderChip(option, () => onRemoveChip(option.value))
          : <DefaultChip key={String(option.value)} option={option} onRemove={() => onRemoveChip(option.value)} />
      )}
    </>
  )
}

// ============================================================================
// Clear button & chevron
// ============================================================================

export function ClearButton({ onClear }: { onClear: (e: React.MouseEvent | React.KeyboardEvent) => void }): JSX.Element {
  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onClear(e) }
  }
  return (
    <span
      aria-label="Clear all"
      className="p-1 hover:bg-accent rounded text-muted-foreground cursor-pointer"
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); onClear(e) }}
      onKeyDown={handleKeyDown}
    >
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path
          clipRule="evenodd"
          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
          fillRule="evenodd"
        />
      </svg>
    </span>
  )
}

export function ChevronIcon({ isOpen }: { isOpen: boolean }): JSX.Element {
  return (
    <svg
      className={clsx('w-4 h-4 text-muted-foreground transition-transform', isOpen && 'rotate-180')}
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        clipRule="evenodd"
        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
        fillRule="evenodd"
      />
    </svg>
  )
}
